@import "tailwindcss";
@import "tw-animate-css";
@import "../vendor/shadcn-tailwind-4.13.0.css";

@utility scrollbar-none {
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}

:root,
[data-theme="dark"] {
  color-scheme: dark;
  --background: #000000;
  --foreground: #ffffff;
  --card: #0d0d0d;
  --card-foreground: #ffffff;
  --popover: #151515;
  --popover-foreground: #ffffff;
  --primary: #e4bb62;
  --primary-foreground: #17120a;
  --secondary: #191919;
  --secondary-foreground: #ffffff;
  --muted: #191919;
  --muted-foreground: #a3a3a3;
  --accent: #242424;
  --accent-foreground: #ffffff;
  --destructive: #ef5350;
  --border: #ffffff1a;
  --input: #ffffff1f;
  --ring: #ddb45c;
  --surface-inset: #ffffff0a;
  --top-bar: #000000eb;
  --bottom-bar: #000000f2;
  --radius: 0.75rem;
}

[data-theme="light"] {
  color-scheme: light;
  --background: #ffffff;
  --foreground: #111111;
  --card: #f7f7f8;
  --card-foreground: #111111;
  --popover: #ffffff;
  --popover-foreground: #111111;
  --primary: #8a5a00;
  --primary-foreground: #ffffff;
  --secondary: #ececec;
  --secondary-foreground: #111111;
  --muted: #ececec;
  --muted-foreground: #676767;
  --accent: #e7e7e7;
  --accent-foreground: #111111;
  --destructive: #c62828;
  --border: #0000001f;
  --input: #00000024;
  --ring: #8a5a00;
  --surface-inset: #0000000a;
  --top-bar: #fffffff0;
  --bottom-bar: #fffffff5;
}

@theme inline {
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);
  --font-sans: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

* { box-sizing: border-box; }
html {
  background: var(--background);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricPrecision;
}
body {
  margin: 0;
  background: var(--background);
  color: var(--foreground);
  font-family: var(--font-sans);
  overscroll-behavior-y: none;
  overflow-x: hidden;
}
#root {
  min-height: 100dvh;
  overflow-x: clip;
}
button, input, textarea { font: inherit; }
input::placeholder,
textarea::placeholder {
  color: color-mix(in srgb, var(--foreground) 48%, transparent);
  opacity: 1;
}
button:disabled,
input:disabled,
textarea:disabled {
  cursor: not-allowed;
  opacity: 0.52;
}
button {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
button:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--ring) 70%, transparent);
  outline-offset: 2px;
}
::selection { background: color-mix(in srgb, var(--primary) 35%, transparent); color: var(--foreground); }

[data-theme="light"] .text-emerald-300,
[data-theme="light"] .text-emerald-200 { color: #047857 !important; }
[data-theme="light"] .text-amber-100,
[data-theme="light"] .text-amber-200,
[data-theme="light"] .text-amber-300 { color: #92400e !important; }


/*
 * RUNYI universal mobile workspace contract.
 * These rules attach to the actual enterprise viewport (and its New
 * Infrastructure portal), never to a static/mock phone frame. They handle
 * registered future provider/status strings as well as existing records.
 */
:where(.runyi-enterprise-screen, #runyi-uicp) {
  box-sizing: border-box;
  inline-size: 100%;
  min-inline-size: 0;
  max-inline-size: 100%;
  overflow-x: clip;
  scroll-padding-block-start: calc(4rem + env(safe-area-inset-top, 0px));
  scroll-padding-block-end: calc(6rem + env(safe-area-inset-bottom, 0px));
}

:where(.runyi-enterprise-screen, #runyi-uicp) :where(.grid) > * {
  min-inline-size: 0;
}

:where(.runyi-enterprise-screen, #runyi-uicp)
  :where([data-slot="card"], [data-slot="card-content"]) {
  min-inline-size: 0;
  max-inline-size: 100%;
}

:where(.runyi-enterprise-screen, #runyi-uicp) :where(.flex.justify-between) {
  /* Backend-generated labels and actions must not force a fixed row width. */
  flex-wrap: wrap;
}

:where(.runyi-enterprise-screen, #runyi-uicp)
  :where([data-slot="badge"]) {
  max-inline-size: 100%;
  min-inline-size: 0;
  block-size: auto;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
}

:where(.runyi-enterprise-screen, #runyi-uicp)
  :where(p, h1, h2, h3, h4) {
  overflow-wrap: anywhere;
}

:where(.runyi-enterprise-screen, #runyi-uicp)
  .runyi-responsive-status-row {
  max-inline-size: 100%;
  min-inline-size: 0;
}

:where(.runyi-enterprise-screen, #runyi-uicp)
  .runyi-responsive-status-row .runyi-status-copy {
  min-inline-size: 0;
  overflow-wrap: anywhere;
}

:where(.runyi-enterprise-screen, #runyi-uicp)
  .runyi-responsive-status-row .runyi-status-meta {
  min-inline-size: 0;
  max-inline-size: 100%;
}
